import React, { useState, useEffect } from 'react';

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit', year: '2-digit'});
};

const FAQItem = ({ faq, onEdit, onDelete }) => {
  return (
    <div className="faq-item">
      <div className="faq-question">{faq.question}</div>
      <div className="faq-answer">{faq.answer}</div>
      <div className="faq-meta">
        <div className="faq-meta-left">
          <span className="faq-category">{faq.category}</span>
          <span className="faq-date">{formatDate(faq.created_at)}</span>
        </div>
        <div className="faq-actions">
          <button className="btn btn-edit" onClick={() => onEdit(faq)} title="Редактировать">
            ✏️
          </button>
          <button className="btn btn-danger btn-delete" onClick={() => onDelete(faq.id)} title="Удалить">
            🗑️
          </button>
        </div>
      </div>
    </div>
  );
};

const PageSizeSelector = ({ itemsPerPage, onItemsPerPageChange, totalItems }) => {
  const options = [5, 10, 20, 50];
  
  return (
    <div className="page-size-selector">
      <select 
        value={itemsPerPage} 
        onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
        className="page-size-select"
      >
        {options.map(option => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span className="page-size-info">из {totalItems}</span>
    </div>
  );
};

const SearchBox = ({ onSearch, onShowAll }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = () => {
    onSearch(searchQuery);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    onShowAll();
  };

  return (
    <div className="faq-search-box">
      <input
        type="text"
        placeholder="Поиск по вопросам и ответам..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyPress={handleKeyPress}
        className="search-input"
      />
      <div className="search-buttons">
        <button className="search-btn" onClick={handleSearch} title="Поиск">
          Поиск
        </button>
        {searchQuery && (
          <button className="clear-btn" onClick={handleClear} title="Очистить">
            Очистить
          </button>
        )}
      </div>
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange, itemsPerPage, totalItems }) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);
    
    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        {startItem}-{endItem} из {totalItems}
      </div>
      <div className="pagination">
        <button 
          className="pagination-btn" 
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          title="Предыдущая страница"
        >
          ←
        </button>
        
        {getPageNumbers().map(pageNumber => (
          <button
            key={pageNumber}
            className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
            onClick={() => onPageChange(pageNumber)}
          >
            {pageNumber}
          </button>
        ))}
        
        <button 
          className="pagination-btn" 
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          title="Следующая страница"
        >
          →
        </button>
      </div>
    </div>
  );
};

const FAQList = ({ faqs, loading, onEdit, onDelete, onSearch, onShowAll }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    setCurrentPage(1);
  }, [faqs]);

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="card">
        <div className="faq-list-header">
          <h2>📝 FAQ ({faqs.length})</h2>
          <SearchBox onSearch={onSearch} onShowAll={onShowAll} />
          <PageSizeSelector
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={faqs.length}
          />
        </div>
        <div className="loading">Загрузка...</div>
      </div>
    );
  }

  if (faqs.length === 0) {
    return (
      <div className="card">
        <div className="faq-list-header">
          <h2>📝 FAQ</h2>
          <SearchBox onSearch={onSearch} onShowAll={onShowAll} />
          <PageSizeSelector
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={faqs.length}
          />
        </div>
        <div className="loading">Ничего не найдено</div>
      </div>
    );
  }

  const totalPages = Math.ceil(faqs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentFAQs = faqs.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    document.querySelector('.faq-list')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="card">
      <div className="faq-list-header">
        <h2>📝 FAQ ({faqs.length})</h2>
        <SearchBox onSearch={onSearch} onShowAll={onShowAll} />
        <PageSizeSelector
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          totalItems={faqs.length}
        />
      </div>
      
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        itemsPerPage={itemsPerPage}
        totalItems={faqs.length}
      />
      
      <div className="faq-list">
        {currentFAQs.map(faq => (
          <FAQItem
            key={faq.id}
            faq={faq}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
      
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        itemsPerPage={itemsPerPage}
        totalItems={faqs.length}
      />
    </div>
  );
};

export default FAQList; 